import { QRCodeSVG } from "qrcode.react";
import type { CardCanvasProps } from "./types";
import { DOMAIN_COLORS } from "./constants";
import { getCardBg } from "./styleHelpers";
import CardLogo from "./CardLogo";
import WatermarkLogo from "./WatermarkLogo";
import CardDecorations from "./CardDecorations";
import TopDomainsBadges from "./TopDomainsBadges";
import { LinkedInStrengthRow } from "./StrengthRows";

/** LinkedIn banner card — English / LTR (1200×630) */
export default function LinkedInCardLTR({
  userName,
  strengths,
  topDomains,
  platformUrl,
  profileUrl,
  cardRef,
  template,
}: CardCanvasProps) {
  const top10 = strengths.slice(0, 10);
  const left5  = top10.slice(0, 5);
  const right5 = top10.slice(5, 10);

  return (
    <div
      ref={cardRef}
      style={{
        width: 1200,
        height: 630,
        background: getCardBg(template, "linkedin"),
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      <CardDecorations template={template} width={1200} height={630} isInstagram={false} />
      <WatermarkLogo />

      {/* Left info panel */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 340,
          borderRight: "1px solid rgba(255,255,255,0.08)",
          padding: "44px 36px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Brand block */}
        <div>
          <div style={{ marginBottom: 28, display: "flex", justifyContent: "flex-start" }}>
            <CardLogo size={48} />
          </div>
          <div style={{ fontSize: 11, letterSpacing: 3, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", marginBottom: 8 }}>
            CliftonStrengths Profile
          </div>
          <div style={{ fontSize: 26, fontWeight: template === "minimal" ? 600 : 800, lineHeight: 1.2, marginBottom: 6 }}>
            {userName}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>Top 10 Strengths</div>
        </div>

        {/* Top domains */}
        <TopDomainsBadges topDomains={topDomains} isRTL={false} compact />

        {/* Domain legend */}
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", marginBottom: 10 }}>
            Domains
          </div>
          {Object.entries(DOMAIN_COLORS).map(([domain, color]) => (
            <div key={domain} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: color }} />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{domain}</span>
            </div>
          ))}
        </div>

        {/* QR */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
          <div style={{ background: "#fff", padding: 8, borderRadius: 10 }}>
            <QRCodeSVG value={profileUrl} size={66} level="M" />
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>Scan to explore</div>
        </div>
      </div>

      {/* Right panel — two explicit flex columns (ranks 1–5 | ranks 6–10) */}
      <div
        style={{
          position: "absolute",
          left: 340,
          top: 0,
          right: 0,
          bottom: 0,
          padding: "44px 36px",
          display: "flex",
          flexDirection: "row",
          gap: 16,
          alignItems: "center",
        }}
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: template === "minimal" ? 0 : 10 }}>
          {left5.map((s) => <LinkedInStrengthRow key={s.rank} s={s} template={template} isRTL={false} />)}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: template === "minimal" ? 0 : 10 }}>
          {right5.map((s) => <LinkedInStrengthRow key={s.rank} s={s} template={template} isRTL={false} />)}
        </div>
      </div>
    </div>
  );
}
